import { IconAirplane42, IconAirplaneHotel42, IconCabin42, IconCarRent42, IconHotel42 } from '@warp-ds/icons/react';
import { Attention } from '@warp-ds/react/components/attention';
import { Tab, Tabs } from '@warp-ds/react/components/tabs';
import type { MouseEvent, MutableRefObject, ReactElement } from 'react';
import { useRef } from 'react';
import { t } from '../locale/texts';
import { SearchVerticalConfig, type SearchVerticalConfigProps, type Vertical } from '../search/common/config';
import styles from './Menu.module.css';

export type MenuProps = {
    activeVertical?: string;
    setVertical?: (event: MouseEvent<HTMLButtonElement>, vertical: Vertical) => void;
    unleash?: Record<string, boolean | string> | null; // cannot be just undefined as that is not serializable (toggles are returned from getSSP)
};

export const Menu = ({ activeVertical, setVertical, unleash }: MenuProps) => {
    const isCarSale = !!unleash?.carRentalSale;
    const targetEl = useRef(null);

    return (
        <Tabs className={`${styles.menu} s-border`} data-testid="travel-menu">
            <MenuItem
                config={SearchVerticalConfig.flight}
                title={t('verticals.flight')}
                activeVertical={activeVertical}
                setVertical={setVertical}>
                <IconAirplane42 aria-hidden="true" className="hidden md:block" />
            </MenuItem>
            <MenuItem
                config={SearchVerticalConfig.hotel}
                title={t('verticals.hotel')}
                activeVertical={activeVertical}
                setVertical={setVertical}
                targetEl={targetEl}>
                <IconHotel42 aria-hidden="true" className="hidden md:block" />
            </MenuItem>
            {activeVertical !== SearchVerticalConfig.hotel.vertical ? (
                <div style={{ zIndex: 1 }}>
                    <Attention highlight isShowing placement="bottom" targetEl={targetEl}>
                        <p>Ny og bedre!</p>
                    </Attention>
                </div>
            ) : null}
            <MenuItem
                config={SearchVerticalConfig.carrental}
                title={t('verticals.car')}
                activeVertical={activeVertical}
                setVertical={setVertical}>
                <div>
                    <IconCarRent42 aria-hidden="true" className="hidden md:block" />
                    {isCarSale && <div className={styles.carSale} data-testid="car-sale" />}
                </div>
            </MenuItem>
            <MenuItem
                config={SearchVerticalConfig.prepackage}
                title={t('verticals.prepackage_dynamic')}
                activeVertical={activeVertical}
                setVertical={setVertical}>
                <IconAirplaneHotel42 aria-hidden="true" className="hidden md:block" />
            </MenuItem>
            <MenuItem
                config={SearchVerticalConfig.fhh}
                title={t('verticals.fhh_short')}
                activeVertical={activeVertical}
                setVertical={setVertical}>
                <IconCabin42 aria-hidden="true" className="hidden md:block" />
            </MenuItem>
        </Tabs>
    );
};

type MenuItemProps = {
    config: SearchVerticalConfigProps;
    title: string;
    activeVertical?: string;
    setVertical?: (event: MouseEvent<HTMLButtonElement>, vertical: Vertical) => void;
    targetEl?: MutableRefObject<HTMLElement | null>;
    children: ReactElement;
};

const MenuItem = ({ config, title, activeVertical, setVertical, targetEl, children }: MenuItemProps) => {
    const handleClick = (event: MouseEvent<HTMLButtonElement>): void => {
        if (setVertical) {
            setVertical(event, config.vertical);
        } else {
            window.location.assign(config.fullPath);
        }
    };

    return (
        <Tab
            // @ts-expect-error Not sure why Tab does not have this prop
            ref={targetEl}
            name={config.vertical}
            label={title}
            over
            isActive={config.vertical === activeVertical}
            onClick={handleClick}
            data-testid={`tab-${config.vertical}`}
            className={styles.tabText}>
            {children}
        </Tab>
    );
};
